import React from 'react';
import {RouterProvider, createBrowserRouter} from 'react-router-dom';

import {Auth}           from '../screens/Auth';
import {Expired}        from '../screens/Expired';
import {TabNavigator}   from './TabNavigator';
import {useSessionGuard} from '../hooks/useSessionGuard';
import {components}     from '../components';

/**
 * GuardedApp — wraps the entire authenticated section of the app.
 *
 * It must be a component (not inline JSX) so that hooks work correctly
 * inside the RouterProvider context.
 *
 * Behaviour:
 * - Synchronously evaluates the stored token before the first render.
 *   If the token is absent, malformed, or expired → returns null while
 *   useSessionGuard fires the redirect. No protected screen is ever rendered.
 * - While active, listens for cross-tab token removal (storage event) and
 *   tab re-focus after idling (visibilitychange), and terminates the session
 *   if trust in the token has been lost for any reason.
 */
const GuardedApp: React.FC = () => {
  const {valid} = useSessionGuard();

  // Return nothing until we know the session is trustworthy.
  // The redirect is handled inside useSessionGuard.
  if (!valid) return null;

  return (
    <>
      <TabNavigator />
      <components.InstallPrompt />
    </>
  );
};

const stack = createBrowserRouter([
  // Public routes — no auth required
  {path: '/auth',    element: <Auth />},
  {path: '/expired', element: <Expired />},

  // Protected route — every render validates the session
  {path: '/',        element: <GuardedApp />},

  // Catch-all — treat any unknown path as session-ended
  {path: '*',        element: <Expired />},
]);

export const StackNavigator: React.FC = () => {
  return <RouterProvider router={stack} />;
};
